(() => {
  'use strict';

  /*
   * pch-moengage-event-forwarder.js   (v1 standardized model)
   *
   * Forwards a small, standardized set of engagement events (Games +
   * Watch & Win) to MoEngage via the Web SDK's track_event, and the same
   * events (identical names + attributes) to Klaviyo via the onsite
   * klaviyo.js track command.
   *
   * V1 EVENTS (5):
   *   Engagement Started   <- module_viewed (game + watch_and_win)
   *   Entries Earned       <- base/grace_entry_result (game), entry_result (w&w)  [success]
   *   Bonus Earned         <- bonus_claim_result (game)                            [success]
   *   Ad Watched           <- ad_completed (game + watch_and_win)
   *   Daily Limit Reached  <- base/grace_entry_result / entry_result              [capped]
   *
   * STANDARD ATTRIBUTES (same shape on every event):
   *   experience_category  "game" | "watch_and_win"
   *   experience_name      <game_name> | "watch_and_win"
   *   entries_today        daily entry total (number)
   *   plays_today          engagement/play count today (number)
   *   bonus_tier           "ad_watch" | "T1" | "T2"  (only on Ad Watched /
   *                        Bonus Earned; omitted where it has no meaning)
   *
   * INTAKE
   *   Wraps window.logClientEvent (the single point all events pass through).
   *   entries_today / plays_today are injected onto every event by the games
   *   orchestrator and the W&W logEvent wrapper (see source edits). bonus_tier
   *   is derived here from the bonus event's entries_earned (3 -> T1, 5 -> T2).
   *
   * IDENTITY
   *   No customer_id is passed. The MoEngage Web SDK attributes events to the
   *   current identified user (or an anonymous device profile pre-login, merged
   *   on login).
   *   Klaviyo only records client-side events for identified profiles, so the
   *   loader snippet identifies logged-in customers by email before any track.
   *
   * LOAD ORDER / SAFETY
   *   Load after sumo-client.js and the MoEngage Web SDK. Resilient to load
   *   order (polls for logClientEvent). Fail-open everywhere; never blocks the
   *   host call or the page.
   *
   * DEBUG
   *   window.__PCH_MOE_DEBUG = true  to trace map/track decisions.
   */

  if (window.__PCH_MOE_FORWARDER) return;
  window.__PCH_MOE_FORWARDER = true;

  const log = (...args) => {
    if (!window.__PCH_MOE_DEBUG) return;
    try { console.log('[pch-moe]', ...args); } catch (e) {}
  };

  // ---------- helpers ----------

  // "watchwin" -> "watch_and_win"; everything else (games, games_grid) -> "game"
  const category = (module) => (module === 'watchwin' ? 'watch_and_win' : 'game');

  // Games carry game_id; W&W has no game, so it self-labels.
  const experienceName = ({ module, game_id: gameId }) => {
    if (module === 'watchwin') return 'watch_and_win';
    return gameId || null;
  };

  const toNumber = (value) => {
    if (value == null || value === '') return undefined;
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  // Mid tier awards 3 entries, max tier awards 5 (consistent across all games).
  // This is the only contest-shaped assumption; swap it when the mapped tier
  // layer lands and you want tier names sourced from there instead.
  const TIER_BY_ENTRIES = { 3: 'T1', 5: 'T2' };
  const tierFromEntries = (entries) => TIER_BY_ENTRIES[toNumber(entries)];

  // Drop only null/undefined. Keep false and 0.
  const clean = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null));

  // The standardized attribute envelope, identical shape on every event.
  // bonus_tier is passed in (undefined where it has no meaning).
  const standardAttrs = (opts, bonusTier) =>
    clean({
      experience_category: category(opts.module),
      experience_name: experienceName(opts),
      entries_today: toNumber(opts.entries_today),
      plays_today: toNumber(opts.plays_today),
      bonus_tier: bonusTier
    });

  // Engagement Started can fire more than once per load (page_view +
  // module_viewed, and some sections double-fire). Emit once per surface.
  const seenStarts = new Set();

  // ---------- raw event -> v1 event ----------
  // Returns { name, attributes } to forward, or null to drop.
  const buildEvent = (opts) => {
    const { result } = opts;
    const extra = opts.extra || {};
    let name = null;
    let bonusTier;

    switch (opts.event_name) {

      case 'module_viewed':
        name = 'Engagement Started';
        break;

      // Games entries: base_entry_result / grace_entry_result
      // W&W entry:     entry_result
      case 'base_entry_result':
      case 'grace_entry_result':
      case 'entry_result':
        if (result === 'success') name = 'Entries Earned';
        else if (result === 'capped') name = 'Daily Limit Reached';
        else return null;
        break;

      case 'bonus_claim_result':
        if (result !== 'success') return null;
        name = 'Bonus Earned';
        bonusTier = tierFromEntries(extra.entries_earned);
        break;

      case 'ad_completed':
        name = 'Ad Watched';
        bonusTier = 'ad_watch';
        break;

      // page_view, ad_started, *_attempted, game_end, fullscreen toggles,
      // and all diagnostics stay in Sumo only.
      default:
        return null;
    }

    if (name === 'Engagement Started') {
      const surfaceKey = `${category(opts.module)}|${experienceName(opts) || '_'}`;
      if (seenStarts.has(surfaceKey)) return null;
      seenStarts.add(surfaceKey);
    }

    return { name, attributes: standardAttrs(opts, bonusTier) };
  };

  // ---------- MoEngage emit ----------
  const trackMoengage = (name, attributes) => {
    const moengage = window.Moengage;
    if (!moengage || typeof moengage.track_event !== 'function') {
      log('SDK not ready, skipped:', name);
      return;
    }
    try {
      moengage.track_event(name, attributes || {});
      log('tracked:', name, attributes);
    } catch (e) {
      log('track_event threw (ignored):', e && e.message);
    }
  };

  // ---------- Klaviyo emit ----------
  // Same event names + attributes as MoEngage so the two platforms line up.
  // Auth is the public site key baked into klaviyo.js by the Klaviyo onsite
  // app embed — nothing secret lives here. Before klaviyo.js loads, calls go
  // onto window._klOnsite, the queue klaviyo.js drains on boot, so early
  // events are not lost. Klaviyo drops client-side events for anonymous
  // visitors; identity is handled in snippets/moengage-forwarder.liquid.
  const trackKlaviyo = (name, attributes) => {
    const command = ['track', name, attributes || {}];
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(command);
      } else {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(command);
      }
      log('klaviyo tracked:', name, attributes);
    } catch (e) {
      log('klaviyo track threw (ignored):', e && e.message);
    }
  };

  // Fan out to every destination; each sink fails independently.
  const emit = (name, attributes) => {
    trackMoengage(name, attributes);
    trackKlaviyo(name, attributes);
  };

  // ---------- install: wrap logClientEvent ----------
  let installed = false;

  const wrap = () => {
    if (installed) return true;
    if (typeof window.logClientEvent !== 'function') return false;

    const originalLogClientEvent = window.logClientEvent;
    // Regular function (not arrow) so the host's `this` passes through.
    window.logClientEvent = function (...args) {
      try {
        const event = buildEvent(args[0] || {});
        if (event) emit(event.name, event.attributes);
      } catch (e) {
        log('forward failed (ignored):', e && e.message);
      }
      return originalLogClientEvent.apply(this, args);
    };

    installed = true;
    log('wrapper installed');
    return true;
  };

  if (!wrap()) {
    const MAX_POLL_ATTEMPTS = 50;
    let pollAttempts = 0;
    const pollTimer = setInterval(() => {
      pollAttempts++;
      if (wrap() || pollAttempts > MAX_POLL_ATTEMPTS) clearInterval(pollTimer);
    }, 100);
  }
})();
